import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from './client'

/**
 * Admin Phones — TanStack hooks for the per-org VAPI phone provisioning UI.
 *
 * Mirrors the shape of ``admin-stripe.ts``: read hooks via ``useQuery``, write
 * hooks via ``useMutation`` with targeted ``invalidateQueries`` on success.
 *
 * Error surfacing: the shared ``apiFetch`` helper throws
 * ``Error("API error <status>: <body>")``. For 409s on
 * ``PUT /api/admin/orgs/{id}/phone`` (1:1 cardinality conflicts) the backend
 * embeds a structured ``detail`` block; ``parsePhoneAssignError`` digs the
 * ``conflict_org_id`` + ``message`` back out so the UI can show inline guidance.
 */

// ── Shapes ─────────────────────────────────────────────────────────────────

export type PhoneAssistantKind = 'inbound' | 'outbound' | 'both'

export interface AdminPhoneAssistantBinding {
  assistant_id: string
  assistant_label: string | null
  kind: PhoneAssistantKind
  created_at: number
}

export interface AdminPhoneNumber {
  id: string
  e164: string
  label: string | null
  provider: string | null
  last_synced_at: number
  assigned_org_id: string | null
  assigned_org_name: string | null
  // Backend includes ``assigned_org_slug``; the UI doesn't render it today
  // but it's safer to type it than to rely on it being absent.
  assigned_org_slug: string | null
  assistants: AdminPhoneAssistantBinding[]
}

export interface AdminPhonesResponse {
  phones: AdminPhoneNumber[]
}

export interface AdminVapiAssistant {
  id: string
  name: string
}

export interface AdminVapiAssistantsResponse {
  assistants: AdminVapiAssistant[]
}

export interface AdminOrgProvisioningRow {
  // Backend returns ``org_id`` (NOT ``id`` like the task description claimed);
  // matches phone_assignments.py:218.
  org_id: string
  name: string
  slug: string | null
  stripe_subscription_id: string | null
  stripe_subscription_status: string | null
  vapi_phone_number_id: string | null
  // Org-level voice provider ('vapi' | 'retell'). Lets the Voice provider
  // control show the saved value on load (phone_assignments.py surfaces it).
  provider: string
  phone_e164: string | null
  phone_label: string | null
  has_inbound_assistant: boolean
  has_outbound_assistant: boolean
}

export interface AdminOrgsProvisioningResponse {
  orgs: AdminOrgProvisioningRow[]
}

// ── Query keys ─────────────────────────────────────────────────────────────

const QK_PHONES = ['admin', 'phones', 'numbers'] as const
const QK_ASSISTANTS = ['admin', 'phones', 'vapi-assistants'] as const
const QK_ORGS = ['admin', 'phones', 'orgs-provisioning'] as const

const ADMIN_BASE = '/api/admin'

// ── Reads ──────────────────────────────────────────────────────────────────

export function useAdminPhoneNumbers() {
  return useQuery({
    queryKey: QK_PHONES,
    queryFn: () =>
      apiFetch<AdminPhonesResponse>(`${ADMIN_BASE}/phone-numbers`),
  })
}

export function useAdminVapiAssistants() {
  return useQuery({
    queryKey: QK_ASSISTANTS,
    queryFn: () =>
      apiFetch<AdminVapiAssistantsResponse>(`${ADMIN_BASE}/vapi-assistants`),
    staleTime: 5 * 60_000,
  })
}

export function useAdminOrgsProvisioning() {
  return useQuery({
    queryKey: QK_ORGS,
    queryFn: () =>
      apiFetch<AdminOrgsProvisioningResponse>(
        `${ADMIN_BASE}/orgs-provisioning`,
      ),
  })
}

// ── Mutations ──────────────────────────────────────────────────────────────

export interface SyncPhonesResponse {
  upserted: number
  phone_ids: string[]
}

export function useSyncPhoneNumbers() {
  const qc = useQueryClient()
  return useMutation({
    // Backend returns the upsert summary; the refreshed phone list is fetched
    // by ``useAdminPhoneNumbers`` after we invalidate the cache.
    mutationFn: () =>
      apiFetch<SyncPhonesResponse>(`${ADMIN_BASE}/phone-numbers/sync`, {
        method: 'POST',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_PHONES })
      qc.invalidateQueries({ queryKey: QK_ORGS })
    },
  })
}

export interface AddPhoneAssistantArgs {
  phoneId: string
  assistant_id: string
  assistant_label?: string | null
  kind: PhoneAssistantKind
}

export function useAddPhoneAssistant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      phoneId,
      assistant_id,
      assistant_label,
      kind,
    }: AddPhoneAssistantArgs) =>
      apiFetch<{
        ok: boolean
        created: boolean
        binding: AdminPhoneAssistantBinding & { phone_id: string; id: string }
      }>(`${ADMIN_BASE}/phone-numbers/${phoneId}/assistants`, {
        method: 'POST',
        body: JSON.stringify({ assistant_id, assistant_label, kind }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_PHONES })
      qc.invalidateQueries({ queryKey: QK_ORGS })
    },
  })
}

export interface RemovePhoneAssistantArgs {
  phoneId: string
  assistant_id: string
  kind: PhoneAssistantKind
}

export function useRemovePhoneAssistant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      phoneId,
      assistant_id,
      kind,
    }: RemovePhoneAssistantArgs) =>
      apiFetch<{ ok: boolean; removed: boolean }>(
        `${ADMIN_BASE}/phone-numbers/${phoneId}/assistants/${encodeURIComponent(
          assistant_id,
        )}?kind=${encodeURIComponent(kind)}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_PHONES })
      qc.invalidateQueries({ queryKey: QK_ORGS })
    },
  })
}

export interface AssignOrgPhoneArgs {
  orgId: string
  phone_id: string | null
}

export function useAssignOrgPhone() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ orgId, phone_id }: AssignOrgPhoneArgs) =>
      apiFetch<{
        ok: boolean
        changed: boolean
        vapi_phone_number_id: string | null
      }>(`${ADMIN_BASE}/orgs/${orgId}/phone`, {
        method: 'PUT',
        body: JSON.stringify({ phone_id }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK_ORGS })
      qc.invalidateQueries({ queryKey: QK_PHONES })
    },
  })
}

// ── Error parsing ──────────────────────────────────────────────────────────

export interface PhoneAssignErrorDetail {
  status: number
  error?: string
  message?: string
  conflict_org_id?: string
}

/**
 * Pull the structured ``detail`` payload back out of an ``apiFetch`` Error.
 *
 * ``apiFetch`` flattens the response to ``Error("API error <status>: <body>")``.
 * For 409 ``phone_already_assigned`` the body is ``{"detail": {...}}`` (FastAPI
 * default), so we reconstruct ``status`` + ``detail`` for the UI to render
 * inline.
 *
 * Returns ``null`` if the error is not in the expected shape.
 */
export function parsePhoneAssignError(
  err: unknown,
): PhoneAssignErrorDetail | null {
  if (!(err instanceof Error)) return null
  const m = err.message.match(/^API error (\d+):\s*(.*)$/s)
  if (!m) return null
  const status = Number(m[1])
  const tail = m[2]
  try {
    const parsed = JSON.parse(tail)
    const detail = parsed?.detail ?? parsed
    if (detail && typeof detail === 'object') {
      return {
        status,
        error: typeof detail.error === 'string' ? detail.error : undefined,
        message:
          typeof detail.message === 'string' ? detail.message : undefined,
        conflict_org_id:
          typeof detail.conflict_org_id === 'string'
            ? detail.conflict_org_id
            : undefined,
      }
    }
  } catch {
    // Body is not JSON — fall through to a status-only return so the caller
    // can still render the raw message.
  }
  return { status }
}
